import IncomingCallIcon from '@/components/icons/callFlow/IncomingCallIcon.vue'
import HangUpIcon from '@/components/icons/callFlow/HangUpIcon.vue'
import BusinessHoursIcon from '@/components/icons/callFlow/BusinessHoursIcon.vue'
import MenuIcon from '@/components/icons/callFlow/MenuIcon.vue'
import VoicemailIcon from '@/components/icons/callFlow/VoicemailIcon.vue'
import PlaybackAudioIcon from '@/components/icons/callFlow/PlaybackAudioIcon.vue'
import CallForwardIcon from '@/components/icons/callFlow/CallForwardIcon.vue'
import CallerInputIcon from '@/components/icons/callFlow/CallerInputIcon.vue'
import PathsIcon from '@/components/icons/callFlow/PathsIcon.vue'
import ProfileIcon from '@/components/icons/callFlow/ProfileIcon.vue'
import GeoRoutingIcon from '@/components/icons/callFlow/GeoRoutingIcon.vue'
import BlaFilterIcon from '@/components/icons/callFlow/BlaFilterIcon.vue'
import TcpaLitigatorIcon from '@/components/icons/callFlow/TcpaLitigatorIcon.vue'
import HttpRouterIcon from '@/components/icons/callFlow/HttpRouterIcon.vue'
import ScriptIcon from '@/components/icons/callFlow/ScriptIcon.vue'

import { DUPLICATE_SCOPE_OPTIONS, SEND_DUPLICATE_OPTIONS } from '@/constants/sendDuplicateOptions'
import { ROUTE_CALLS } from '@/constants/call-tracking/call-flow/geoRoutingRouteCalls'
import { ZIP_CODE_FETCHING } from '@/constants/call-tracking/call-flow/geoRoutingFetchType'
import { REQUEST_METHODS } from '@/constants/requestMethods'

const INCOMING_CALL_COMPONENT = {
    id: 1,
    name: 'Incoming Call',
    config: {
        valid: true,
    },
    paths: [],
    background: '#1CBF5D',
    defaultHeight: 42,
    icon: () => IncomingCallIcon,
}

const CALL_FLOW_COMPONENTS = {
    menu: {
        id: 3,
        name: 'Menu',
        outputs: {
            key_1: null,
            no_input: null,
            wrong_input: null,
        },
        config: {
            valid: false,
            interrupt_audio: false,
            timeout: 15,
            retries: 1,
            audio_url: null,
            description: null,
            language: null,
            voice: null,
        },
        paths: [],
        background: '#A290E7',
        defaultHeight: 162,
        info: 'An interactive voice response menu allowing the caller to select further actions by pressing keys',
        icon: () => MenuIcon,
    },
    callerInput: {
        id: 8,
        name: 'Caller Input',
        outputs: {
            success: null,
            no_input: null,
            wrong_input: null,
        },
        config: {
            valid: false,
            parameter_name: null,
            audio_url: null,
            description: null,
            language: null,
            voice: null,
            interrupt_audio: false,
            min_digits: 1,
            max_digits: 9,
            valid_digits: '1234567890',
            end_digit: '#',
            enter_timeout: 3,
            timeout: 10,
            retries: 3,
        },
        background: '#7FD4A1',
        defaultHeight: 122,
        info: 'Collects numerical input from the caller',
        icon: () => CallerInputIcon,
    },
    playbackAudio: {
        id: 2,
        name: 'Playback Audio',
        outputs: {
            prompt_completed: null,
        },
        config: {
            valid: false,
            audio_url: null,
            description: null,
            language: null,
            voice: null,
        },
        paths: [],
        background: '#568FE5',
        defaultHeight: 42,
        info: 'Plays an audio file for the caller before proceeding with further actions',
        icon: () => PlaybackAudioIcon,
    },
    voicemail: {
        id: 4,
        name: 'Voicemail',
        outputs: {
            recording_completed: null,
        },
        config: {
            valid: false,
            audio_url: null,
            description: null,
            language: null,
            voice: null,
            beep: false,
            timeout: 15,
            end_digit: '*',
            max_length: 15,
            transcription: false,
            voicemail_url: null,
            voicemail_email: null,
        },
        paths: [],
        background: '#FF922E',
        defaultHeight: 42,
        info: 'Records a voicemail message from the caller',
        icon: () => VoicemailIcon,
    },
    callForward: {
        id: 5,
        name: 'Call Forward',
        outputs: {
            busy: null,
            failed: null,
            no_answer: null,
        },
        config: {
            valid: false,
            duplicate_route_id: SEND_DUPLICATE_OPTIONS.normally.id,
            queue_enabled: false,
            duplicates_strict: false,
            duplicate_scope_id: DUPLICATE_SCOPE_OPTIONS.destination.id,
            queue_time_limit: 5,
            queue_audio: null,
            revenue_recovery_enabled: false,
            revenue_recovery_threshold: 1,
            revenue_recovery_attempts: 1,
            whisper_message_enabled: false,
            whisper_message_type_id: null,
            whisper_message_url: null,
            whisper_message_text: null,
            whisper_message_language: null,
            whisper_message_voice: null,
        },
        paths: [],
        background: '#FFBF1D',
        defaultHeight: 162,
        info: 'Redirects the call to another phone number or department',
        icon: () => CallForwardIcon,
    },
    hangUp: {
        id: 6,
        name: 'Hang Up',
        outputs: {},
        config: {
            valid: true,
        },
        paths: [],
        background: '#F64C54',
        defaultHeight: 42,
        info: 'Ends the call and disconnects the caller',
        icon: () => HangUpIcon,
    },
    paths: {
        id: 9,
        name: 'Paths',
        outputs: {
            other: null,
        },
        config: {
            valid: true,
        },
        paths: [],
        background: '#D47FCC',
        defaultHeight: 122,
        info: 'Routes calls based on customizable conditions',
        icon: () => PathsIcon,
    },
    httpRouter: {
        id: 14,
        name: 'HTTP Router',
        outputs: {
            success: null,
            failure: null,
        },
        config: {
            timeout: 2000,
            valid: false,
            request_type: REQUEST_METHODS.get.id,
            request_url: '',
            response_parsing: [],
        },
        paths: [],
        background: '#A7FFEB',
        defaultHeight: 122,
        info: 'Routes calls based on customizable conditions',
        icon: () => HttpRouterIcon,
    },
    geoRouting: {
        id: 10,
        name: 'Geo-Routing',
        outputs: {
            any_other_locations: null,
        },
        config: {
            valid: true,
            routing_type: ROUTE_CALLS.areaCode.id,
            fetching_type: ZIP_CODE_FETCHING.input.id,
            params: {
                zip_code_prompt_enabled: false,
                audio_url: null,
                description: null,
                voice: null,
                language: null,
                min_digits: 1,
                max_digits: 1,
                valid_digits: '1234567890',
                end_digit: '#',
                enter_timeout: 3,
                interrupt_audio: false,
                timeout: 10,
                retries: 3,
                parameters: [],
            },
            input: {
                audio_url: null,
                description: null,
                voice: null,
                language: null,
                min_digits: 1,
                max_digits: 1,
                valid_digits: '1234567890',
                end_digit: '#',
                enter_timeout: 3,
                interrupt_audio: false,
                timeout: 10,
                retries: 3,
            },
            locations: [],
        },
        paths: [],
        background: '#568FE5',
        defaultHeight: 122,
        info: 'Routes calls based on GEO information',
        icon: () => GeoRoutingIcon,
    },
    blaFilter: {
        id: 12,
        name: 'BlackListAlliance',
        outputs: {
            allowed: null,
            blocked: null,
            failure: null,
        },
        config: {
            api_key: null,
            timeout: 1500,
        },
        paths: [],
        background: '#BF5FFF',
        defaultHeight: 162,
        info: 'Checks the caller\'s number through BlackListAlliance',
        icon: () => BlaFilterIcon,
    },
    tcpaLitigator: {
        id: 13,
        name: 'TCPA Litigator',
        outputs: {
            allowed: null,
            blocked: null,
            failure: null,
        },
        config: {
            login: null,
            password: null,
            timeout: 1500,
        },
        paths: [],
        background: '#FF8552',
        defaultHeight: 162,
        info: 'Checks the caller\'s number through TCPA Litigator',
        icon: () => TcpaLitigatorIcon,
    },
    profile: {
        id: 11,
        name: 'Caller Identity',
        outputs: {
            success: null,
            failure: null,
        },
        config: {
            valid: true,
        },
        paths: [],
        background: '#10DEED',
        defaultHeight: 122,
        info: 'An extra cost applies to this component',
        confirmComponent: {
            title: 'Extra cost warning',
            description: 'This component uses external data enrichment services and incurs additional charges',
            actionCancel: 'removeLastBlock',
        },
        icon: () => ProfileIcon,
    },
    businessHours: {
        id: 7,
        name: 'Business Hours',
        outputs: {
            open: null,
            closed: null,
        },
        config: {
            schedule: null,
            timezone_id: 1,
            valid: true,
        },
        paths: [],
        background: '#D47FCC',
        defaultHeight: 122,
        info: 'Defines working hours for handling calls depending on the time of day',
        icon: () => BusinessHoursIcon,
    },
    scriptNode: {
        id: 15,
        name: 'JS Param Modifier',
        outputs: {
            success: null,
            failure: null,
        },
        config: {
            script: '',
        },
        paths: [],
        background: '#7FD4A1',
        defaultHeight: 122,
        info: 'Routes calls based on customizable conditions',
        icon: () => ScriptIcon,
    },
}

const CALL_FLOW_ALL_COMPONENTS = {
    INCOMING_CALL_COMPONENT,
    ...CALL_FLOW_COMPONENTS,
}

export {
    CALL_FLOW_COMPONENTS,
    INCOMING_CALL_COMPONENT,
    CALL_FLOW_ALL_COMPONENTS,
}
